import React from "react";
import PropTypes from "prop-types";

// @mui material components
import Menu from "@mui/material/Menu";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

// Custom Components & API
// TODO: Update these paths to match your project structure
import { readAllNotifications } from "services/api";
import NotificationMenu from "examples/Navbars/DashboardNavbar/components/NotificationItem";
import { useTranslation } from "react-i18next";

function NotificationsMenu({
  anchorEl,
  open,
  onClose,
  notifications,
  setNotifications,
  employeeId,
  darkMode,
}) {
  const { t, i18n } = useTranslation();
  // Handle "Mark All Read" logic
  const handleMarkAllRead = async () => {
    if (!employeeId) return;

    try {
      const res = await readAllNotifications(employeeId);
      if (res !== "error") {
        setNotifications((prev) =>
          prev.map((notif) =>
            notif.read ? notif : { ...notif, read: true, readAt: new Date().toISOString() }
          )
        );
      }
    } catch (e) {
      console.error("Failed to mark notifications read", e);
    }
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(open)}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{
        sx: {
          mt: 1.5,
          borderRadius: "20px",
          width: "320px",
          maxHeight: "500px",
          boxShadow: "0 15px 50px rgba(0,0,0,0.15)",
          overflow: "hidden",
        },
      }}
    >
      {/* Header Section */}
      <MDBox
        px={2.5}
        py={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        backgroundColor={darkMode ? "rgba(255,255,255,0.03)" : "#fbfbfb"}
      >
        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: "1rem" }}>
          {t("notifications.title")}
        </Typography>
        {notifications.some((n) => !n.read) && (
          <MDButton
            variant="text"
            color="info"
            size="small"
            sx={{ fontSize: "0.7rem", fontWeight: "bold" }}
            onClick={handleMarkAllRead}
          >
            {t("notifications.mark")}
          </MDButton>
        )}
      </MDBox>

      <Divider sx={{ m: 0 }} />

      {/* List Section */}
      <MDBox sx={{ overflowY: "auto", maxHeight: "400px", py: 1 }}>
        {notifications.length === 0 ? (
          <MDBox py={4} textAlign="center">
            <Icon sx={{ fontSize: "40px !important", color: "grey.300", mb: 1 }}>
              notifications_off
            </Icon>
            <Typography variant="button" display="block" color="textSecondary">
              {t("notifications.no")}
            </Typography>
          </MDBox>
        ) : (
          notifications.map((n, index) => (
            <Box key={n.id} sx={{ px: 1 }}>
              <NotificationMenu
                n={n}
                setNotifications={setNotifications}
                handleNotificationsClose={onClose}
              />
              {index !== notifications.length - 1 && (
                <Divider sx={{ mx: 2, my: 0.5, opacity: 0.5 }} />
              )}
            </Box>
          ))
        )}
      </MDBox>
    </Menu>
  );
}

// Typechecking props
NotificationsMenu.propTypes = {
  anchorEl: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  notifications: PropTypes.arrayOf(PropTypes.object).isRequired,
  setNotifications: PropTypes.func.isRequired,
  employeeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  darkMode: PropTypes.bool,
};

NotificationsMenu.defaultProps = {
  anchorEl: null,
  darkMode: false,
};

export default NotificationsMenu;
